/**
 * ActionPreviewPanel — confirmation step before any Excel write.
 *
 * Pattern:  click → read target → show preview → confirm → apply
 *
 * Shows:
 *   - Impact summary (target range, cells affected)
 *   - Optional before/after values
 *   - Warnings
 *   - Confirm (primary) + Cancel actions
 *   - Loading + success + error states
 *
 * Used by FormulaCard, CleaningPreviewCard, AnalysisPage, ChartPage.
 */

import React, { useState, useCallback } from 'react';
import { Button, Text, Badge, MessageBar, MessageBarBody } from '@fluentui/react-components';
import {
  CheckmarkRegular,
  DismissRegular,
  ArrowDownloadRegular,
} from '@fluentui/react-icons';

// ─── Types ──────────────────────────────────────────────

export interface PreviewImpact {
  /** Target range or cell address (e.g. "Feuil1!A1", "A1:C10"). */
  readonly target: string;
  /** Human-readable description of what will happen. */
  readonly description: string;
  /** Number of cells affected. */
  readonly cellsAffected?: number;
  /** Current value of the target cell(s) — displayed as "before". */
  readonly currentValue?: string;
  /** New value that will be written — displayed as "after". */
  readonly newValue?: string;
}

export interface ActionPreviewPanelProps {
  /** Impact details. */
  readonly impact: PreviewImpact;
  /** Optional extra warnings. */
  readonly warnings?: readonly string[];
  /** Called when user confirms — should perform the Excel write. */
  readonly onConfirm: () => Promise<void>;
  /** Called when user cancels. */
  readonly onCancel: () => void;
  /** Label for the confirm button. Default: "Appliquer". */
  readonly confirmLabel?: string;
  /** Icon for the confirm button. */
  readonly confirmIcon?: React.ReactElement;
  /** Extra content to render between impact and actions. */
  readonly children?: React.ReactNode;
}

type PanelState = 'idle' | 'loading' | 'success' | 'error';

// ─── Component ──────────────────────────────────────────

export const ActionPreviewPanel: React.FC<ActionPreviewPanelProps> = ({
  impact,
  warnings,
  onConfirm,
  onCancel,
  confirmLabel = 'Appliquer',
  confirmIcon,
  children,
}) => {
  const [state, setState] = useState<PanelState>('idle');
  const [errorMsg, setErrorMsg] = useState<string>();

  const handleConfirm = useCallback(async () => {
    setState('loading');
    setErrorMsg(undefined);
    try {
      await onConfirm();
      setState('success');
    } catch (err) {
      setErrorMsg((err as Error).message);
      setState('error');
    }
  }, [onConfirm]);

  // After success, auto-dismiss after a short delay
  if (state === 'success') {
    return (
      <div className="action-preview">
        <div className="action-preview-success">
          <CheckmarkRegular />
          <Text size={200} weight="semibold">Action appliquee avec succes</Text>
        </div>
      </div>
    );
  }

  return (
    <div className="action-preview">
      {/* Impact summary */}
      <div className="action-preview-impact">
        <div className="action-preview-row">
          <Text size={200} style={{ color: 'var(--text-secondary)' }}>Cellule cible</Text>
          <Badge appearance="outline" size="small" color="informative">
            {impact.target}
          </Badge>
          {impact.cellsAffected != null && impact.cellsAffected > 1 && (
            <Badge appearance="outline" size="small" color="informative">
              {impact.cellsAffected} cellule{impact.cellsAffected > 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        <Text size={200} style={{ color: 'var(--text-secondary)' }}>
          {impact.description}
        </Text>

        {/* Before/After */}
        {(impact.currentValue != null || impact.newValue != null) && (
          <div className="action-preview-diff">
            {impact.currentValue != null && (
              <div className="action-preview-diff-row">
                <Text size={200} style={{ color: 'var(--text-secondary)', minWidth: 50 }}>Avant</Text>
                <code className="action-preview-value action-preview-value--before">
                  {impact.currentValue || '(vide)'}
                </code>
              </div>
            )}
            {impact.newValue != null && (
              <div className="action-preview-diff-row">
                <Text size={200} style={{ color: 'var(--text-secondary)', minWidth: 50 }}>Apres</Text>
                <code className="action-preview-value action-preview-value--after">
                  {impact.newValue}
                </code>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Extra content */}
      {children}

      {/* Warnings */}
      {warnings && warnings.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {warnings.map((w, i) => (
            <MessageBar key={i} intent="warning" style={{ borderRadius: 4 }}>
              <MessageBarBody>{w}</MessageBarBody>
            </MessageBar>
          ))}
        </div>
      )}

      {/* Error */}
      {state === 'error' && errorMsg && (
        <MessageBar intent="error" style={{ borderRadius: 4 }}>
          <MessageBarBody>{errorMsg}</MessageBarBody>
        </MessageBar>
      )}

      {/* Actions */}
      <div className="action-preview-actions">
        <Button
          size="small"
          appearance="primary"
          icon={confirmIcon ?? <ArrowDownloadRegular />}
          onClick={handleConfirm}
          disabled={state === 'loading'}
        >
          {state === 'loading' ? 'Application...' : confirmLabel}
        </Button>
        <Button
          size="small"
          appearance="subtle"
          icon={<DismissRegular />}
          onClick={onCancel}
          disabled={state === 'loading'}
        >
          Annuler
        </Button>
      </div>
    </div>
  );
};
